import { useMemo, useState, useEffect } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Image as ImageIcon,
  Move,
  Settings,
  Clipboard,
  CheckCircle,
  Shield,
  MapPin
} from "react-feather";
import Alert from "../../../../components/Alert/Alert";
import useBackConfirm from "../../../../hooks/useBackConfirm";
import ProjectReviewApi from "../../../../api/ProjectReviewApi";
import { UserProjectsApi } from "../../../../api/UserProjectsApi";
import { saveProjectAuth, getProjectAuth, clearProjectAuth } from "../../../../utils/projectAuthSession";
import { getSession } from "../../../../utils/authSession";

import "./ProjectReview.css";

export default function ProjectReview({
  signs = [],
  removalAreas = {},
  reviewProject = null,
  onBack,
  onGenerate
}) {

  /* ---------- GROUPING ---------- */
  const grouped = useMemo(() => {
    const map = new Map();

    (signs || []).forEach((s, idx) => {
      const imageIndex = Number.isFinite(s?.imageIndex) ? s.imageIndex : 0;

      if (!map.has(imageIndex)) {
        map.set(imageIndex, {
          imageIndex,
          signs: []
        });
      }

      map.get(imageIndex).signs.push({ ...s });
    });

    return Array.from(map.values()).sort((a, b) => a.imageIndex - b.imageIndex);
  }, [signs]);

  /* ---------- INSTALLATION ---------- */
  const [installationRequired, setInstallationRequired] = useState(false);
  const [address, setAddress] = useState("");

  /* ---------- STATES ---------- */
  // const [email, setEmail] = useState(
  //   localStorage.getItem("localUserEmail") ||
  //   localStorage.getItem("lastUsedEmail") ||
  //   ""
  // );

  const session = getSession();

  const [email] = useState(session?.email || "");

  const [projectName, setProjectName] = useState(
    reviewProject?.projectName || ""
  );

  const [revisionInstructions, setRevisionInstructions] = useState("");

  const [revisionImageIndex, setRevisionImageIndex] = useState(0);

  const [imgDims, setImgDims] = useState({});

  const backConfirm = useBackConfirm(onBack);

  const emailValid = ProjectReviewApi.isValidEmail(email);
  const addressValid = !installationRequired || address.trim().length >= 8;

  // const canOpenVerify = emailValid && !verified && !isSendingCode;
  const projectNameValid = projectName.trim().length > 0;

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const [showRevisionInfo, setShowRevisionInfo] = useState(false);

  const canSubmit =
    signs.length > 0 &&
    addressValid &&
    projectNameValid &&
    (!reviewProject || revisionInstructions.trim().length > 0);

  const SUBMIT_STEPS = [
    { icon: "📄", text: "Generating Scope of Work" },
    { icon: "📐", text: "Generating estimated costs, dimensions and details" },
    { icon: "🧠", text: "Generating render / visual with AI" },
    { icon: "📦", text: "Generating Project" },
    { icon: "✉️", text: "Sending email with project details" }
  ];

  const submitHint = useMemo(() => {
    if (!signs.length) return "No signs found.";
    // if (!verified) return "Validate your email to submit.";
    if (!projectNameValid) return "Project name is required.";
    if (!addressValid) return "Enter a valid installation address.";
    return "";
  }, [signs, addressValid, projectNameValid]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  /* ===== BUILD IMAGE WITH LOGOS ===== */
  function buildPreparedImage(imgGroup) {

    if (reviewProject?.images) {

      const image = reviewProject.images.find(
        i => Number(i.imageIndex) === Number(imgGroup.imageIndex)
      );

      const imageUrl =
        image?.baseImage?.url ||
        image?.renderImage?.url ||
        image?.preparedImage?.url ||
        null;

      if (imageUrl) {
        return Promise.resolve(imageUrl);
      }

    }

    return new Promise((resolve) => {
      const base = imgGroup.signs[0]?.baseImage;
      if (!base) return resolve(null);

      const img = new Image();
      img.src = base;

      img.onload = async () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");

        // base
        ctx.drawImage(img, 0, 0);

        /* ===== REMOVAL AREAS (DRAWN INTO IMAGE) ===== */
        const removals = removalAreas?.[imgGroup.imageIndex] || [];

        removals.forEach((r) => {
          const scaleX = canvas.width / (imgGroup.signs[0]?.stageWidth || canvas.width);
          const scaleY = canvas.height / (imgGroup.signs[0]?.stageHeight || canvas.height);

          const x = r.x * scaleX;
          const y = r.y * scaleY - 10;
          const w = r.w * scaleX;
          const h = r.h * scaleY;
          const rotation = r.rotation || 0;

          const SCALE = 1.08;
          const drawW = w * SCALE;
          const drawH = h * SCALE;

          ctx.save();

          // center + rotation
          ctx.translate(x + w / 2, y + h / 2);
          ctx.rotate((rotation * Math.PI) / 180);

          // fill
          ctx.fillStyle = "rgba(209, 10, 10, 0.76)";
          ctx.fillRect(-drawW / 2, -drawH / 2, drawW, drawH);

          // dashed border
          ctx.strokeStyle = "#b000005d";
          ctx.setLineDash([8, 6]);
          ctx.lineWidth = 5;
          ctx.strokeRect(-drawW / 2, -drawH / 2, drawW, drawH);
          ctx.setLineDash([]);

          // centered label (replacement for <span>Removal area</span>)
          ctx.fillStyle = "#fff";
          ctx.font = `bold ${Math.max(13, drawH * 0.11)}px system-ui`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText("Removal", 0, -8);
          ctx.fillText("Area", 0, 12);

          ctx.restore();
        });


        const logoPromises = imgGroup.signs.map((s, index) => {
          if (!s.logo?.base64 || !s.shape) return null;

          return new Promise((res) => {
            const logo = new Image();
            logo.src = s.logo.base64;

            logo.onload = () => {
              // const { x, y, w, h, rotation = 0 } = s.shape;

              const scaleX = canvas.width / s.stageWidth;
              const scaleY = canvas.height / s.stageHeight;

              const x = s.shape.x * scaleX;
              const y = s.shape.y * scaleY;
              const w = s.shape.w * scaleX;
              const h = s.shape.h * scaleY;
              const rotation = s.shape.rotation || 0;

              // const scaleX = canvas.width / s.shapeImageWidth;
              // const scaleY = canvas.height / s.shapeImageHeight;

              const baseW = w;
              const baseH = h;

              const centerX = x + w / 2;
              const centerY = y + h / 2;

              ctx.save();
              ctx.translate(centerX, centerY);
              ctx.rotate((rotation * Math.PI) / 180);

              /* ===== CIRCLE CLIP ===== */
              if (s.shape.type === "circle") {
                ctx.save();
                ctx.beginPath();
                ctx.arc(0, 0, Math.min(baseW, baseH) / 2, 0, Math.PI * 2);
                ctx.closePath();
                ctx.clip();
              }

              /* ===== LOGO FIT ===== */
              const paddingRatio = 0.01;
              const maxW = baseW * (1 - paddingRatio);
              const maxH = baseH * (1 - paddingRatio);

              const logoRatio = logo.width / logo.height;
              const boxRatio = maxW / maxH;

              let drawW, drawH;

              if (logoRatio > boxRatio) {
                drawW = maxW;
                drawH = maxW / logoRatio;
              } else {
                drawH = maxH;
                drawW = maxH * logoRatio;
              }

              ctx.drawImage(
                logo,
                -drawW / 2,
                -drawH / 2,
                drawW,
                drawH
              );

              if (s.shape.type === "circle") {
                ctx.restore();
              }

              /* ===== BADGE ===== */
              const badgeRadius = Math.max(14, Math.min(baseW, baseH) * 0.12);
              const badgeY = -baseH / 2 - badgeRadius - 8;

              ctx.fillStyle = "#1e6bff";
              ctx.beginPath();
              ctx.arc(0, badgeY, badgeRadius, 0, Math.PI * 2);
              ctx.fill();

              ctx.fillStyle = "#fff";
              ctx.font = `bold ${badgeRadius}px system-ui`;
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              ctx.fillText(String(index + 1), 0, badgeY);

              ctx.restore();
              res();
            };


          });
        });

        await Promise.all(logoPromises.filter(Boolean));

        const MAX_WIDTH = 1150;

        if (canvas.width > MAX_WIDTH) {

          const scale = MAX_WIDTH / canvas.width;

          const resizedCanvas = document.createElement("canvas");

          resizedCanvas.width = MAX_WIDTH;
          resizedCanvas.height = Math.round(canvas.height * scale);

          const resizedCtx = resizedCanvas.getContext("2d");

          resizedCtx.drawImage(
            canvas,
            0,
            0,
            resizedCanvas.width,
            resizedCanvas.height
          );

          resolve(
            resizedCanvas.toDataURL("image/jpeg", 0.8)
          );

        } else {

          resolve(
            canvas.toDataURL("image/jpeg", 0.8)
          );

        }
      };
    });
  }

  /* ===== INSIDE ProjectReview COMPONENT ===== */

  const [preparedImages, setPreparedImages] = useState({});

  useEffect(() => {
    async function run() {
      const map = {};
      for (const g of grouped) {
        map[g.imageIndex] = await buildPreparedImage(g);
      }
      setPreparedImages(map);
    }
    run();
  }, [grouped, reviewProject]);

  useEffect(() => {
    if (reviewProject) {
      setShowRevisionInfo(true);
    }
  }, [reviewProject]);

  /* ---------- ACTIONS ---------- */

  function sanitizeProjectName(value) {
    if (!value) return "";
    return value
      .trim()
      .replace(/[^a-zA-Z0-9 _-]/g, "") // remove chars inválidos
      .replace(/\s+/g, " ")           // normaliza espaços
      .slice(0, 40);
  }

  // const groupedImagesForPayload = useMemo(() => {
  //   return grouped.map((g) => {
  //     const first = g.signs[0] || {};

  //     return {
  //       imageIndex: g.imageIndex,
  //       baseImage: preparedImages[g.imageIndex] || first.baseImage || null,
  //       maskImage: first.maskImage || null,

  //       // 🔥 ADICIONAR ISSO:
  //       removalAreas: removalAreas?.[g.imageIndex] || []
  //     };

  //   });
  // }, [grouped, preparedImages, removalAreas]);

  const groupedImagesForPayload = useMemo(() => {

    const isRevision =
      Boolean(reviewProject);

    if (isRevision) {

      return reviewProject.images.map((img) => ({

        imageIndex: img.imageIndex,

        // imagem original/base
        baseImage:
          img.baseImage?.url ||
          null,

        originalBaseImage:
          img.baseImage?.url ||
          null,

        maskImage:
          img.maskImage?.url ||
          null,

        // último render da AI
        compositePreview:
          img.renderImage?.url ||
          img.preparedImage?.url ||
          null,

        removalAreas:
          img.removalAreas || []

      }));

    }

    return grouped.map((g) => {

      const first = g.signs[0] || {};

      return {

        imageIndex: g.imageIndex,

        baseImage:
          preparedImages[g.imageIndex] ||
          first.baseImage ||
          null,

        maskImage:
          first.maskImage ||
          null,

        removalAreas:
          removalAreas?.[g.imageIndex] || []

      };

    });

  }, [
    grouped,
    preparedImages,
    removalAreas,
    reviewProject,
    revisionInstructions
  ]);


  /* ------- helpers ------- */
  function humanSignType(id) {
    const map = {
      channel: "Channel Letters",
      neon: "Flex Neon Sign",
      alumCut: "Aluminum Letters",
      acrylic3D: "Acrylic Letters",
      pvc: "PVC Letters",
      lightbox: "Light Box",
      push: "Push-Through Sign",
      blade: "Blade Sign",
      pylon: "Complete Pylon",
      film: "Print / Dicut Graphics",
      banner: "Printed Banner",
      printMat: "ACM / Aluminum + Graphics",
      acrylicFace: "Acrylic + Graphics",
      other: "Other"
    };
    return map[id] || id;
  }

  const revisionImages =
    reviewProject?.images
      ?.map(img => img.renderImage?.url)
      .filter(Boolean) || [];

  const currentRevisionImage =
    revisionImages[revisionImageIndex] || null;

  // ---------- RENDER ----------
  return (
    <div className="project-review">
      {/* Top actions */}
      <div className="upload-header">

        <div className="steps-progress">

          <div className="step active">
            <div className="step-icon">
              <ImageIcon size={18} />
            </div>
            <span>Upload Picture</span>
          </div>

          <div className="step-line" />

          <div className="step active">
            <div className="step-icon">
              <Move size={18} />
            </div>
            <span>Add Signs</span>
          </div>

          <div className="step-line" />

          <div className="step active">
            <div className="step-icon">
              <Settings size={18} />
            </div>
            <span>Project Setup</span>
          </div>

          <div className="step-line" />

          <div className="step active">
            <div className="step-icon">
              <Clipboard size={18} />
            </div>
            <span>Project Review</span>
          </div>

          <div className="step-line" />

          <div className="step">
            <div className="step-icon">
              <CheckCircle size={18} />
            </div>
            <span>Results</span>
          </div>

        </div>

        <div className="upload-header-content">

          <div className="studio-header">
            <h2>Review your project</h2>
            <p>Please confirm all details before submitting.</p>
          </div>

          <div className="actions">

            {!reviewProject && (
              <button
                className="secondary"
                onClick={backConfirm.askBack}
              >
                <ArrowLeft size={14} />
                Back
              </button>
            )}

            <button
              className={`pr-btn pr-primary submit-btn ${canSubmit ? "" : "disabled"}`}
              disabled={!canSubmit}
              title={submitHint}
              onClick={() => {
                const payload = {
                  ...ProjectReviewApi.buildProjectPayload({
                    projectName: sanitizeProjectName(projectName),
                    signs,
                    images: groupedImagesForPayload,
                    contact: { email },
                    installationRequired,
                    address,
                    removalAreas,
                    aiRevisionPrompt: revisionInstructions
                  }),

                  projectId:
                    reviewProject?.projectId ||
                    reviewProject?.id ||
                    null,

                  companyId:
                    reviewProject?.companyId ||
                    null,
                };

                onGenerate(payload);
              }}
            >
              Submit Project
              <ArrowRight size={14} />
            </button>

          </div>

        </div>

      </div>

      {(signs || []).length > 0 && (
        <>

          {!reviewProject && (
            <section className="project-top-row">
              {/* PROJECT NAME */}
              <div className="project-top-col project-name-col">
                <h3>Project name</h3>

                <input
                  type="text"
                  value={projectName}
                  className={`input-project-name ${reviewProject
                    ? "readonly-input"
                    : projectName.trim().length < 3
                      ? "pulse-input"
                      : ""
                    }`}
                  onChange={(e) => {
                    if (reviewProject) return;

                    const clean = e.target.value
                      .replace(/[^a-zA-Z0-9 _-]/g, "")
                      .replace(/\s+/g, " ")
                      .slice(0, 40);

                    setProjectName(clean);
                  }}
                  placeholder="My signage project"
                  maxLength={40}
                  readOnly={!!reviewProject}
                />

                {!projectNameValid && (
                  <div className="pr-help">
                    Please enter a valid project name.
                  </div>
                )}

                <p className="project-name-hint">Max 40 characters.</p>
              </div>

              {/* INSTALLATION */}
              <div className="project-top-col installation-col">
                <h3>Installation required?</h3>

                <div className="pr-toggle">
                  <button
                    className={`pr-toggle-btn ${installationRequired ? "active" : ""}`}
                    onClick={() => setInstallationRequired(true)}
                  >
                    Yes
                  </button>

                  <button
                    className={`pr-toggle-btn ${!installationRequired ? "active" : ""}`}
                    onClick={() => {
                      setInstallationRequired(false);
                      setAddress("");
                    }}
                  >
                    No
                  </button>
                </div>

                {installationRequired && (
                  <div className="pr-field">
                    <label>
                      <MapPin size={14} />
                      Installation address
                    </label>
                    <input
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Street, City, Province, Postal code"
                      className={!addressValid ? "invalid" : ""}
                    />
                    {!addressValid && (
                      <div className="pr-help">Please enter a valid address.</div>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}
        </>
      )}

      {reviewProject && (

        <div className="pr-revision-box">

          <div className="pr-revision-preview">

            {currentRevisionImage && (
              <>
                <img
                  src={currentRevisionImage}
                  alt="Current AI Render"
                  className="pr-render-preview"
                />

                {revisionImages.length > 1 && (

                  <div className="pr-render-nav">

                    <button
                      type="button"
                      onClick={() =>
                        setRevisionImageIndex(i =>
                          Math.max(i - 1, 0)
                        )
                      }
                      disabled={revisionImageIndex === 0}
                    >
                      ←
                    </button>

                    <span>
                      {revisionImageIndex + 1} / {revisionImages.length}
                    </span>

                    <button
                      type="button"
                      onClick={() =>
                        setRevisionImageIndex(i =>
                          Math.min(i + 1, revisionImages.length - 1)
                        )
                      }
                      disabled={
                        revisionImageIndex === revisionImages.length - 1
                      }
                    >
                      →
                    </button>

                  </div>

                )}

              </>
            )}

          </div>

          <div className="pr-revision-editor">

            <h3>AI Revision Instructions</h3>

            <textarea
              className={!revisionInstructions.trim() ? "required" : ""}
              value={revisionInstructions}
              onChange={(e) => setRevisionInstructions(e.target.value)}
              placeholder="The previous AI render is shown on the left for reference. Describe the final result you want to achieve from the original project setup below."
            />

            {!revisionInstructions.trim() && (
              <div className="revision-required">
                Revision instructions are required before AI can generate a new version.
              </div>
            )}

          </div>

        </div>

      )}

      {/* <hr></hr> */}

      {/* Empty state */}
      {(!signs || signs.length === 0) && (
        <div className="pr-empty">
          <div className="pr-empty-card">
            <h3>No signs to review</h3>
            <p>
              Go back to the setup steps and add at least one sign area, then select a sign type and logo.
            </p>
            <button className="pr-btn pr-secondary" onClick={backConfirm.askBack}>
              <ArrowLeft size={14} /> Back to setup
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      {(signs || []).length > 0 && (
        <div className="pr-grid">
          {/* LEFT: images + signs */}
          <section className="pr-section">
            <div className="pr-section-title">
              <h2>Project images & signs</h2>
              <span className="pr-pill">{signs.length} signs</span>
            </div>

            <div className="pr-images">
              {grouped.map((imgGroup) => (

                <div key={imgGroup.imageIndex} className="pr-image-card">

                  <div className="pr-image-wrapper">
                    <img
                      src={preparedImages[imgGroup.imageIndex]}
                      className="pr-composite-image"
                      alt=""
                      onLoad={(e) => {
                        const el = e.currentTarget;
                        setImgDims((prev) => ({
                          ...prev,
                          [imgGroup.imageIndex]: {
                            w: el.clientWidth,
                            h: el.clientHeight,
                            nw: el.naturalWidth,
                            nh: el.naturalHeight
                          }
                        }));
                      }}
                    />

                  </div>

                  {/* REMOVAL LIST (KEEP THIS ✅) */}
                  {removalAreas?.[imgGroup.imageIndex]?.length > 0 && (
                    <div className="pr-removal-list">
                      <div className="pr-removal-title">
                        Removal area
                      </div>

                      {removalAreas[imgGroup.imageIndex].map((r, idx) => (
                        <div key={r.id} className="pr-removal-item">
                          Removal area {idx + 1}
                        </div>
                      ))}
                    </div>
                  )}

                </div>


              ))}
            </div>
          </section>

          {/* RIGHT: details + install + email */}
          <section className="pr-section">
            <div className="pr-section-title">
              <h2>Sign details</h2>
              <span className="pr-note">Read-only summary</span>
            </div>

            <div className="pr-details">
              {(signs || []).map((s, idx) => (
                <div key={s.id || idx} className="pr-detail-card">
                  <div className="pr-detail-row">
                    <div className="pr-detail-title">
                      <h3>{s.label || `Sign ${idx + 1}`}</h3>
                      <span className="pr-muted">
                        Image {Number.isFinite(s.imageIndex) ? s.imageIndex + 1 : 1} • Area{" "}
                        {Number.isFinite(s.shapeIndex) ? s.shapeIndex + 1 : idx + 1}
                      </span>
                    </div>

                    <div className="pr-badges">
                      <span className="pr-chip">
                        {s.signType ? humanSignType(s.signType) : "Type not selected"}
                      </span>
                      <span className={`pr-chip ${s.illuminated ? "blue" : "gray"}`}>
                        {s.illuminated ? "Illuminated" : "Non-illuminated"}
                      </span>
                      {s.addition ? (
                        <span className="pr-chip dashed">+ {s.addition}</span>
                      ) : (
                        <span className="pr-chip ghost">No additions</span>
                      )}
                    </div>
                  </div>

                  <div className="pr-detail-row pr-detail-body">
                    <div className="pr-mini">
                      <div className="pr-mini-label">Dimensions</div>
                      <div className="pr-mini-value">
                        {(s.width && s.height && Number(s.width) > 0 && Number(s.height) > 0) ? (
                          <>
                            {s.width} in × {s.height} in
                          </>
                        ) : (
                          <span className="pr-muted">Estimate with AI</span>
                        )}
                      </div>
                    </div>

                    <div className="pr-mini">
                      <div className="pr-mini-label">
                        Logo / Artwork
                      </div>

                      <div className="pr-mini-value">
                        <span className="pr-ok">
                          {reviewProject ? "Uploaded" : (s.logo ? "Uploaded" : "Missing")}
                        </span>
                      </div>
                    </div>

                    <div className="pr-mini">
                      <div className="pr-mini-label">AI Mode</div>
                      <div className="pr-mini-value">
                        {s.aiMode ? <span className="pr-warn">Generate with AI</span> : <span className="pr-ok">Manual</span>}
                      </div>
                    </div>
                  </div>

                  {!reviewProject && s.logo?.base64 && (
                    <div className="pr-logo-preview">
                      <div className="pr-mini-label">Artwork preview</div>
                      <div className="pr-logo-box">
                        <img src={s.logo.base64} alt="logo preview" />
                      </div>
                    </div>
                  )}
                  {s.instructions && s.instructions.trim().length > 0 && (
                    <div className="pr-instructions" style={{ fontSize: '14px' }}>
                      <div className="pr-mini-label" style={{ fontSize: '11px', marginTop: '10px' }}>Sign instructions:</div>
                      <div className="pr-instructions-box">
                        {s.instructions}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

          </section>
        </div>
      )}

      {/* Footer actions */}
      <div className="pr-actions pr-actions-bottom">
        <button className="pr-btn pr-secondary" onClick={backConfirm.askBack}>
          <ArrowLeft size={14} /> Back
        </button>

        <button
          className={`pr-btn pr-primary submit-btn ${canSubmit ? "" : "disabled"}`}
          onClick={() => {
            const payload = {
              ...ProjectReviewApi.buildProjectPayload({
                projectName: sanitizeProjectName(projectName),
                signs,
                images: groupedImagesForPayload,
                contact: { email },
                installationRequired,
                address,
                removalAreas,
                aiRevisionPrompt: revisionInstructions
              }),

              projectId:
                reviewProject?.projectId ||
                reviewProject?.id ||
                null,

              companyId:
                reviewProject?.companyId ||
                null,
            };

            console.log("🚀 PAYLOAD IMAGES:", payload.images?.map(img => ({
              imageIndex: img.imageIndex,
              hasBaseImage: !!img.baseImage,
              baseStart: img.baseImage?.slice(0, 30),
              hasCompositePreview: !!img.compositePreview,
              compositeStart: img.compositePreview?.slice(0, 30),
              hasPreparedPreview: !!img.preparedPreview,
              hasMask: !!img.maskImage
            })));

            onGenerate(payload);
          }}
          disabled={!canSubmit}
          title={submitHint}
        >
          <CheckCircle size={16} />
          Submit project
        </button>
      </div>

      {/* Back confirm */}
      <Alert
        open={backConfirm.open}
        title="Go back?"
        message="Are you sure you want to go back? Some actions may not have been saved."
        onClose={backConfirm.cancel}
        onConfirm={backConfirm.confirm}
      />

      {/* Verify modal (simple overlay using Alert too) */}
      {/* <Alert
        open={verifyOpen}
        title="Verify your email"
        message={
          "Enter the 6-digit code we sent to your email. (Placeholder: check console for the code)"
        }
        onClose={handleCancelVerify}
        onConfirm={handleConfirmCode}
      /> */}

      {showErrorModal && (
        <div
          className="verification-error-backdrop"
          onClick={() => setShowErrorModal(false)}
        >
          <div
            className="verification-error-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="verification-error-icon">
              ⚠️
            </div>

            <h3>Verification failed</h3>

            <p>{modalMessage}</p>

            <button
              className="verification-error-btn"
              onClick={() => setShowErrorModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {showRevisionInfo && (
        <div
          className="revision-info-backdrop"
          onClick={() => setShowRevisionInfo(false)}
        >
          <div
            className="revision-info-modal"
            onClick={(e) => e.stopPropagation()}
          >

            <div className="revision-info-icon">
              ✨
            </div>

            <h2>AI Revision</h2>

            <p>
              The previous AI render is displayed on this page for reference only.
            </p>

            <p>
              Your new instructions will be applied using the <strong>original project setup</strong>,
              not by editing the displayed render directly.
            </p>

            <div className="revision-info-note">
              💡 Describe the final result you want to achieve as clearly as possible.
            </div>

            <button
              className="revision-info-btn"
              onClick={() => setShowRevisionInfo(false)}
            >
              Continue
            </button>

          </div>
        </div>
      )}

      {/* When verifyOpen, show code input overlay */}
      {/* {verifyOpen && (
        <div className="pr-modal-overlay" onMouseDown={handleCancelVerify}>
          <div className="pr-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="pr-modal-title">Enter verification code</div>
            <div className="pr-modal-sub">
              We sent a 6-digit code to <strong>{email}</strong>.
            </div>

            <input
              className="pr-code"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, "").slice(0, 6))}
              placeholder="123456"
            />

            <div className="pr-modal-actions">
              <button className="pr-btn pr-secondary" onClick={handleCancelVerify}>
                Cancel
              </button>
              <button
                className={`pr-btn pr-primary ${code.trim().length === 6 ? "" : "disabled"}`}
                disabled={code.trim().length !== 6}
                onClick={handleConfirmCode}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )} */}

    </div>
  );
}

